/**
 * How the chain names an account.
 *
 * A chip states what the chain says and does not endorse it. `MintingAccount`
 * is a sentinel with no account entry at all, not a wallet; labelling it as what
 * the runtime calls it is a fact, and implying it holds funds or is trustworthy
 * would be the site vouching for an address it knows nothing about.
 *
 * The title carries the citation, because the chip is the claim and the
 * citation is the evidence — and the three kinds are different claims. A
 * constant is compiled in and immutable for that runtime; a storage value is
 * assigned and changeable; a genesis endowment is history and not a role at all.
 */

import type { AccountRoleEntry } from '../api/generated/AccountRoleEntry'

function why(role: AccountRoleEntry): string {
  switch (role.source) {
    case 'constant':
      return `Named by the runtime constant ${role.cited} — compiled in, and fixed for as long as this runtime is in force.`
    case 'state':
      return `Named by ${role.cited} in chain state — assigned, and changeable by whatever call the pallet provides.`
    case 'genesis':
      return `Held a balance in block zero. History, not a job: an account can be endowed and have no role.`
  }
}

export function RoleChips({ roles }: { roles: AccountRoleEntry[] }) {
  if (roles.length === 0) return null
  // One chip per label. Two constants naming the same address is two citations
  // and one fact, and rendering "Minting Minting" would say otherwise.
  const seen = new Set<string>()
  const shown = roles.filter((r) => !seen.has(r.label) && seen.add(r.label))
  return (
    <>
      {shown.map((r) => (
        <span
          key={r.label}
          className={r.source === 'genesis' ? 'chip chip-genesis' : 'chip chip-role'}
          title={[why(r), r.docs].filter(Boolean).join('\n\n')}
        >
          {r.label}
        </span>
      ))}
    </>
  )
}
